import type { analyticsCardType } from 'BreetAssets';
import type { CURRENCYCODETYPE, IApiResponse, IVIPStatsGraph, IVIPStatsGraphItem } from 'BreetHelpers';

export type { IVIPStatsGraphItem };

export interface IAnalyticsCard {
	label: string;
	value?: string | number;
	stats?: string | number;
	iconVariant: analyticsCardType;
}

export type AnalyticsPeriod = '6months' | '3months' | '1month' | '7days' | '1day';
export type AnalyticsTab = 'Transactions' | 'Withdrawals' | 'Conversions';

export const ANALYTICS_PERIODS: { label: string; value: AnalyticsPeriod }[] = [
	{ label: '6 Months', value: '6months' },
	{ label: '3 Months', value: '3months' },
	{ label: '30 Days', value: '1month' },
	{ label: '7 Days', value: '7days' },
	{ label: '24 Hours', value: '1day' },
];

export const ANALYTICS_CURRENCIES: CURRENCYCODETYPE[] = ['NGN', 'USD', 'GHS'];

export const ANALYTICS_TABS: AnalyticsTab[] = ['Transactions', 'Withdrawals', 'Conversions'];

export const getPeriodDateRange = (period: AnalyticsPeriod): { startDate: string; endDate: string } => {
	const now = new Date();
	const start = new Date(now);

	switch (period) {
		case '6months':
			start.setMonth(start.getMonth() - 6);
			break;
		case '3months':
			start.setMonth(start.getMonth() - 3);
			break;
		case '1month':
			start.setDate(start.getDate() - 30);
			break;
		case '7days':
			start.setDate(start.getDate() - 7);
			break;
		case '1day':
			start.setHours(start.getHours() - 24);
			break;
	}

	return { startDate: start.toISOString(), endDate: now.toISOString() };
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const toDateKey = (d: Date): string =>
	`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Option B — 6months period.
 *
 * Always generates all 6 monthly slots working back from today,
 * then maps API data into the correct slot by (year, month) key.
 * Months with no API data receive a value of 0 so the full 6 months
 * are always visible on the x-axis.
 *
 * A year suffix ("Dec '25") is added whenever the 6-month window
 * straddles two calendar years.
 */
const buildYearChartData = (data: IVIPStatsGraphItem[]): IChartPoint[] => {
	const now = new Date();

	// Build lookup: "YYYY-MM" → total volume (aggregate if multiple entries per month)
	const dataMap = new Map<string, number>();
	for (const point of data) {
		const d = new Date(point.time);
		const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
		dataMap.set(key, (dataMap.get(key) ?? 0) + point.volume);
	}

	// Does the 6-month window span two calendar years?
	const windowStart = new Date(now.getFullYear(), now.getMonth() - 5, 1);
	const isMultiYear = windowStart.getFullYear() !== now.getFullYear();

	// Generate all 6 slots oldest → newest
	const slots: IChartPoint[] = [];
	for (let i = 5; i >= 0; i--) {
		const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
		const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
		const month = MONTHS[d.getMonth()];
		const value = dataMap.get(key) ?? 0;
		// Axis: short month name only ("Jun")
		// Tooltip: month + year suffix ("Jun '25") only when there's data — no need for year context on zero slots
		const tooltipLabel = value > 0 && isMultiYear ? `${month} '${String(d.getFullYear()).slice(2)}` : month;
		slots.push({ label: month, tooltipLabel, value });
	}

	return slots;
};

/**
 * Option B — 3months period.
 *
 * Always generates all 3 monthly slots working back from today,
 * then aggregates API data into the correct slot by (year, month) key.
 * Months with no API data receive a value of 0 so all 3 months are
 * always visible on the x-axis.
 *
 * Axis label  : short month name ("Jan", "Feb", "Mar")
 * Tooltip     : month name only when zero; "Jan '25" when data and window
 *               straddles two calendar years; otherwise just "Jan"
 */
const buildThreeMonthChartData = (data: IVIPStatsGraphItem[]): IChartPoint[] => {
	const now = new Date();

	// Build lookup: "YYYY-MM" → total volume (aggregate all entries in that month)
	const dataMap = new Map<string, number>();
	for (const point of data) {
		const d = new Date(point.time);
		const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
		dataMap.set(key, (dataMap.get(key) ?? 0) + point.volume);
	}

	// Does the 3-month window span two calendar years?
	const windowStart = new Date(now.getFullYear(), now.getMonth() - 2, 1);
	const isMultiYear = windowStart.getFullYear() !== now.getFullYear();

	// Generate 3 slots: oldest → newest
	const slots: IChartPoint[] = [];
	for (let i = 2; i >= 0; i--) {
		const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
		const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
		const month = MONTHS[d.getMonth()];
		const value = dataMap.get(key) ?? 0;
		const tooltipLabel = value > 0 && isMultiYear ? `${month} '${String(d.getFullYear()).slice(2)}` : month;
		slots.push({ label: month, tooltipLabel, value });
	}

	return slots;
};

/**
 * Option B — 1month (30 days) period.
 *
 * Always generates all 30 daily slots working back from today,
 * then aggregates API data into the correct slot by exact date key.
 * Days with no API data receive a value of 0 so all 30 days are
 * always visible on the x-axis.
 *
 * Axis label  : day number only ("1", "2" … "31") — keeps ticks short
 * Tooltip     : "Apr 5" when there's data; day number only when zero
 * Multi-year  : year suffix appended ("Dec 31 '24") when window straddles two years
 */
const buildThirtyDayChartData = (data: IVIPStatsGraphItem[]): IChartPoint[] => {
	const now = new Date();

	// Build lookup: "YYYY-MM-DD" → total volume (aggregate multiple entries per day)
	const dataMap = new Map<string, number>();
	for (const point of data) {
		const key = toDateKey(new Date(point.time));
		dataMap.set(key, (dataMap.get(key) ?? 0) + point.volume);
	}

	// Does the 30-day window span two calendar years?
	const windowStart = new Date(now);
	windowStart.setDate(windowStart.getDate() - 29);
	const isMultiYear = windowStart.getFullYear() !== now.getFullYear();

	// Generate 30 slots: oldest → newest
	const slots: IChartPoint[] = [];
	for (let i = 29; i >= 0; i--) {
		const d = new Date(now);
		d.setDate(d.getDate() - i);
		const key = toDateKey(d);
		const value = dataMap.get(key) ?? 0;
		const month = MONTHS[d.getMonth()];
		const day = d.getDate();

		const defaultLabel = isMultiYear ? `${month} ${day} '${String(d.getFullYear()).slice(2)}` : `${month} ${day}`;

		const tooltipLabel = value > 0 ? defaultLabel : `${month} ${day}`;

		slots.push({ label: `${d.getMonth() + 1}/${day}`, tooltipLabel, value });
	}

	return slots;
};

/**
 * Option B — 1day (24 hours) period.
 *
 * Always generates all 24 hourly slots working back from the current hour,
 * then aggregates API data into the correct slot by hour key.
 * Hours with no API data receive a value of 0 so all 24 hours are
 * always visible on the x-axis.
 *
 * Axis label  : 12-hour format ("12am", "1am" … "11pm")
 * Tooltip     : same as axis label
 */
const buildOneDayChartData = (data: IVIPStatsGraphItem[]): IChartPoint[] => {
	const now = new Date();

	// Build lookup: "YYYY-MM-DD-HH" → total volume
	const dataMap = new Map<string, number>();
	for (const point of data) {
		const d = new Date(point.time);
		const key = `${toDateKey(d)}-${String(d.getHours()).padStart(2, '0')}`;
		dataMap.set(key, (dataMap.get(key) ?? 0) + point.volume);
	}

	const formatHour = (h: number): string => {
		if (h === 0) return '12am';
		if (h === 12) return '12pm';
		return h < 12 ? `${h}am` : `${h - 12}pm`;
	};

	// Generate 24 slots: 23 hours ago → current hour
	const slots: IChartPoint[] = [];
	for (let i = 23; i >= 0; i--) {
		const d = new Date(now);
		d.setHours(d.getHours() - i, 0, 0, 0);
		const key = `${toDateKey(d)}-${String(d.getHours()).padStart(2, '0')}`;
		const label = formatHour(d.getHours());
		slots.push({ label, tooltipLabel: label, value: dataMap.get(key) ?? 0 });
	}

	return slots;
};

/**
 * Option B — 7days period.
 *
 * Always generates all 7 daily slots working back from today,
 * then aggregates API data into the correct slot by exact date key.
 * Days with no API data receive a value of 0 so all 7 days are
 * always visible on the x-axis.
 *
 * Axis label  : short day name ("Mon", "Tue" … "Sun")
 * Tooltip     : same day name
 */
const buildSevenDayChartData = (data: IVIPStatsGraphItem[]): IChartPoint[] => {
	const now = new Date();

	// Build lookup: "YYYY-MM-DD" → total volume
	const dataMap = new Map<string, number>();
	for (const point of data) {
		const key = toDateKey(new Date(point.time));
		dataMap.set(key, (dataMap.get(key) ?? 0) + point.volume);
	}

	// Generate 7 slots: oldest → newest
	const slots: IChartPoint[] = [];
	for (let i = 6; i >= 0; i--) {
		const d = new Date(now);
		d.setDate(d.getDate() - i);
		const key = toDateKey(d);
		const label = DAYS[d.getDay()];
		slots.push({ label, tooltipLabel: label, value: dataMap.get(key) ?? 0 });
	}

	return slots;
};

/**
 * Converts a raw API data array into chart-ready points.
 *
 * - 6months   → always 6 slots (Option B), zeroes for months with no data
 * - 3months → always 3 monthly slots (Option B), zeroes for months with no data
 * - 1month  → always 30 daily slots (Option B), zeroes for days with no data
 * - 7days   → always 7 daily slots (Option B), zeroes for days with no data
 * - 1day    → always 24 hourly slots (Option B), 12-hour labels, zeroes for hours with no data
 */
export const buildChartData = (data: IVIPStatsGraphItem[], period: AnalyticsPeriod): IChartPoint[] => {
	if (period === '6months') return buildYearChartData(data);
	if (period === '3months') return buildThreeMonthChartData(data);
	if (period === '1month') return buildThirtyDayChartData(data);
	if (period === '7days') return buildSevenDayChartData(data);
	return buildOneDayChartData(data);
};

export const formatYAxisTick = (value: number): string => {
	if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(0)}M`;
	if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
	return String(value);
};

export interface IChartPoint {
	label: string; // shown on x-axis tick
	tooltipLabel: string; // shown in hover tooltip
	value: number;
}

// Demo data matching the design screenshot curve (7-day view)
export const DEMO_CHART_DATA_7D: IChartPoint[] = [
	{ label: 'Mon', tooltipLabel: 'Mon', value: 25000 },
	{ label: 'Tue', tooltipLabel: 'Tue', value: 23500 },
	{ label: 'Wed', tooltipLabel: 'Wed', value: 29000 },
	{ label: 'Thu', tooltipLabel: 'Thu', value: 35500 },
	{ label: 'Fri', tooltipLabel: 'Fri', value: 33000 },
	{ label: 'Sat', tooltipLabel: 'Sat', value: 40000 },
	{ label: 'Sun', tooltipLabel: 'Sun', value: 45500 },
];

export const DEMO_CHART_DATA_30D: IChartPoint[] = Array.from({ length: 30 }, (_, i) => ({
	label: `${i + 1}`,
	tooltipLabel: `${i + 1}`,
	value: 20000 + Math.sin(i / 3) * 8000 + i * 600,
}));

export const DEMO_CHART_DATA_3M: IChartPoint[] = [
	{ label: 'Jan', tooltipLabel: 'Jan', value: 28000 },
	{ label: 'Feb', tooltipLabel: 'Feb', value: 34000 },
	{ label: 'Mar', tooltipLabel: 'Mar', value: 31000 },
];

export const DEMO_CHART_DATA_6M: IChartPoint[] = [
	{ label: 'Jul', tooltipLabel: 'Jul', value: 45000 },
	{ label: 'Aug', tooltipLabel: 'Aug', value: 48000 },
	{ label: 'Sep', tooltipLabel: 'Sep', value: 46000 },
	{ label: 'Oct', tooltipLabel: 'Oct', value: 51000 },
	{ label: 'Nov', tooltipLabel: 'Nov', value: 55000 },
	{ label: 'Dec', tooltipLabel: 'Dec', value: 58000 },
];

export const getDemoChartData = (period: AnalyticsPeriod): IChartPoint[] => {
	switch (period) {
		case '7days':
		case '1day':
			return DEMO_CHART_DATA_7D;
		case '1month':
			return DEMO_CHART_DATA_30D;
		case '3months':
			return DEMO_CHART_DATA_3M;
		case '6months':
			return DEMO_CHART_DATA_6M;
		default:
			return DEMO_CHART_DATA_7D;
	}
};

export const formatFullDateRange = (from: string, to: string) => {
	const isDateSelected = from.length && to.length;

	if (!isDateSelected) {
		return 'Last 7 days';
	}

	const msInDay = 1000 * 60 * 60 * 24;
	const fromDate = new Date(from);
	const toDate = new Date(to);

	const diffDays = Math.round((toDate.getTime() - fromDate.getTime()) / msInDay) + 1;

	return `${diffDays} days`;
};

export interface IAnalyticsChart {
	dataLoading: boolean;
	vipStatsGraphData?: IApiResponse<IVIPStatsGraph>;
	activeTab: AnalyticsTab;
	activePeriod: AnalyticsPeriod;
	activeCurrency: CURRENCYCODETYPE;
}

export const rawChartDataObject = (
	vipStatsGraphData?: IApiResponse<IVIPStatsGraph>
): Record<AnalyticsTab, IVIPStatsGraphItem[] | undefined> => ({
	Conversions: vipStatsGraphData?.data?.conversions,
	Transactions: vipStatsGraphData?.data?.sells,
	Withdrawals: vipStatsGraphData?.data?.withdrawals,
});

export const getActivePeriod = (utcStartDate: string, utcEndDate: string): AnalyticsPeriod => {
	const diffMs = new Date(utcEndDate).getTime() - new Date(utcStartDate).getTime();
	const diffDays = diffMs / (1000 * 60 * 60 * 24);

	if (diffDays <= 3.5) return '1day';
	if (diffDays <= 18) return '7days';
	if (diffDays <= 60) return '1month';
	if (diffDays <= 228) return '3months';
	return '6months';
};
